import React, { useState, useEffect, memo } from 'react';
import perfMonitor from '../utils/performance';

const PerformanceDashboard = memo(() => {
  const [isVisible, setIsVisible] = useState(false);
  const [report, setReport] = useState(null);
  const [memoryUsage, setMemoryUsage] = useState(null);

  useEffect(() => {
    // Update performance data every 5 seconds
    const interval = setInterval(() => {
      const currentReport = perfMonitor.getReport();
      setReport(currentReport);
      
      if (performance.memory) {
        const memory = performance.memory;
        setMemoryUsage({
          used: (memory.usedJSHeapSize / 1024 / 1024).toFixed(2),
          total: (memory.totalJSHeapSize / 1024 / 1024).toFixed(2),
          limit: (memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2),
          percentage: ((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100).toFixed(1)
        });
      }
    }, 5000);

    // Check for development environment
    if (process.env.NODE_ENV === 'development') {
      // Show performance dashboard on Ctrl+Shift+P
      const handleKeyPress = (e) => {
        if (e.ctrlKey && e.shiftKey && e.key === 'P') {
          setIsVisible(prev => !prev);
        }
      };
      window.addEventListener('keydown', handleKeyPress);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('keydown', handleKeyPress);
      };
    }

    return () => clearInterval(interval);
  }, []);

  if (!isVisible || process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      width: '400px',
      maxHeight: '80vh',
      background: 'rgba(0, 0, 0, 0.9)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      padding: '16px',
      zIndex: 10000,
      fontSize: '12px',
      color: 'white',
      overflow: 'auto',
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '16px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        paddingBottom: '8px'
      }}>
        <h3 style={{ margin: 0, color: '#00ff88' }}>🚀 Performance Monitor</h3>
        <button 
          onClick={() => setIsVisible(false)}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          ×
        </button>
      </div>

      {memoryUsage && (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#66d9ff' }}>💾 Memory Usage</h4>
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.1)', 
            borderRadius: '4px', 
            padding: '8px',
            fontSize: '11px'
          }}>
            <div>Used: {memoryUsage.used} MB</div>
            <div>Total: {memoryUsage.total} MB</div>
            <div>Limit: {memoryUsage.limit} MB</div>
            <div style={{ 
              color: memoryUsage.percentage > 80 ? '#ff6b6b' : memoryUsage.percentage > 60 ? '#ffd93d' : '#51cf66'
            }}>
              Usage: {memoryUsage.percentage}%
            </div>
          </div>
        </div>
      )}

      {report && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#66d9ff' }}>📊 Summary</h4>
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '4px', 
              padding: '8px',
              fontSize: '11px'
            }}>
              <div>Components Tracked: {report.summary.componentsTracked}</div>
              <div>Total Renders: {report.summary.totalRenders}</div>
              <div>API Calls: {report.summary.totalApiCalls}</div>
              <div>Images Loaded: {report.summary.imagesLoaded}</div>
              <div>Avg Image Load: {report.summary.avgImageLoadTime}ms</div>
            </div>
          </div>

          {report.webVitals && report.webVitals.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#66d9ff' }}>📈 Core Web Vitals</h4>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: '4px', 
                padding: '8px',
                fontSize: '11px'
              }}>
                {report.webVitals.map((vital, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '4px'
                  }}>
                    <span>{vital.name}:</span>
                    <span style={{
                      color: vital.rating === 'good' ? '#51cf66' : 
                             vital.rating === 'needs-improvement' ? '#ffd93d' : '#ff6b6b'
                    }}>
                      {vital.value.toFixed(2)} ({vital.rating})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.keys(report.renders).length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#66d9ff' }}>🎨 Component Performance</h4>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: '4px', 
                padding: '8px',
                fontSize: '11px',
                maxHeight: '150px',
                overflow: 'auto'
              }}>
                {Object.entries(report.renders).map(([component, stats]) => (
                  <div key={component} style={{ marginBottom: '8px' }}>
                    <div style={{ fontWeight: 'bold', color: '#ffd93d' }}>{component}</div>
                    <div style={{ marginLeft: '8px' }}>
                      <div>Avg: {stats.avg.toFixed(2)}ms</div>
                      <div>Max: {stats.max.toFixed(2)}ms</div>
                      <div>Count: {stats.count}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.memoryLeak && (
            <div style={{ 
              marginBottom: '16px',
              background: 'rgba(255, 107, 107, 0.2)',
              border: '1px solid #ff6b6b',
              borderRadius: '4px',
              padding: '8px'
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#ff6b6b' }}>🚨 Memory Leak Detected</h4>
              <div style={{ fontSize: '11px' }}>
                Growth: {(report.memoryLeak.growth / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>
          )}
        </>
      )}

      <div style={{ 
        fontSize: '10px', 
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
        marginTop: '16px',
        paddingTop: '8px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        Press Ctrl+Shift+P to toggle
      </div>
    </div>
  );
});

PerformanceDashboard.displayName = 'PerformanceDashboard';

export default PerformanceDashboard;